import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  fetchListing,
  fetchListingReviews,
  createBooking,
  getMyBuyerAlias,
  registerBuyerAlias,
  fetchSellerBookings,
  fetchReviewStats,
  type Listing,
  type Booking,
  type Review,
} from '../../features/marketplace/api';
import { Button } from '../../components/ui/Button';

export function MarketplaceDetailPage() {
  const navigate = useNavigate();
  const { listingId } = useParams<{ listingId: string }>();
  const [listing, setListing] = useState<Listing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewStats, setReviewStats] = useState<{ advanceSharePerm: boolean } | null>(null);

  useEffect(() => {
    if (listingId) {
      loadListing();
    }
  }, [listingId]);

  useEffect(() => {
    // Attempt to load review stats for gating advanced/superior reviews; ignore errors
    (async () => {
      try {
        const stats = await fetchReviewStats();
        setReviewStats({ advanceSharePerm: stats.advanceSharePerm });
      } catch (err) {
        // leave null; advanced reviews will stay hidden unless permitted
        setReviewStats(null);
      }
    })();
  }, []);

  async function loadListing() {
    if (!listingId) return;

    try {
      setLoading(true);
      const data = await fetchListing(listingId);
      setListing(data);
      const loadedReviews = await fetchListingReviews(listingId, 50);
      setReviews(loadedReviews || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load listing');
    } finally {
      setLoading(false);
    }
  }

  async function handleBooking(buyerAlias: string, scheduledFor: string) {
    if (!listingId) return;

    try {
      await createBooking({ listingId, buyerAlias, scheduledFor });
      setBookingSuccess(true);
      setShowBookingForm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create booking');
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/30 to-slate-50 dark:from-slate-950 dark:via-purple-950/20 dark:to-slate-950">
        <div className="text-center text-slate-600 dark:text-slate-400 py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 dark:border-purple-400 mb-4"></div>
          <p>Loading listing...</p>
        </div>
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/30 to-slate-50 dark:from-slate-950 dark:via-purple-950/20 dark:to-slate-950">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Button onClick={() => navigate('/marketplace')} variant="secondary" className="mb-6 shadow-md hover:shadow-lg transition-all">
            ← Back to Catalog
          </Button>
          <div className="bg-red-100 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-900 rounded-xl p-6 text-red-700 dark:text-red-400 shadow-lg">
            <div className="flex items-center gap-3">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="font-semibold">Error: {error || 'Listing not found'}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/30 to-slate-50 dark:from-slate-950 dark:via-purple-950/20 dark:to-slate-950">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button onClick={() => navigate('/marketplace')} variant="secondary" className="mb-6 shadow-md hover:shadow-lg transition-all">
          ← Back to Catalog
        </Button>

        <div className="bg-white dark:bg-slate-800 border-2 border-purple-200 dark:border-purple-900/50 rounded-2xl p-8 shadow-xl">
          <div className="mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-purple-600 via-fuchsia-600 to-purple-600 dark:from-purple-400 dark:via-fuchsia-400 dark:to-purple-400 bg-clip-text text-transparent mb-6 leading-tight">{listing.title}</h1>

            {/* Service Info Badges */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <div className="flex items-center gap-2 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-900/30 dark:to-green-900/30 border-2 border-emerald-300 dark:border-emerald-700 rounded-xl px-4 py-2 shadow-md">
                <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-emerald-700 dark:text-emerald-300 font-bold text-base">
                  {listing.currency} {(listing.priceCents / 100).toFixed(2)}
                </span>
              </div>

              {listing.durationMinutes && (
                <div className="flex items-center gap-2 bg-gradient-to-r from-purple-50 to-fuchsia-50 dark:from-purple-900/30 dark:to-fuchsia-900/30 border-2 border-purple-300 dark:border-purple-700 rounded-xl px-4 py-2 shadow-md">
                  <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-purple-700 dark:text-purple-300 font-semibold text-sm">
                    {listing.durationMinutes < 60
                      ? `${listing.durationMinutes} minutes`
                      : listing.durationMinutes === 60
                        ? '1 hour'
                        : listing.durationMinutes < 120
                          ? `${(listing.durationMinutes / 60).toFixed(1)} hours`
                          : `${Math.floor(listing.durationMinutes / 60)} hours`
                    }
                  </span>
                </div>
              )}

              <div className="flex items-center gap-2 bg-gradient-to-r from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-800 border-2 border-slate-300 dark:border-slate-600 rounded-xl px-4 py-2 shadow-md">
                <svg className="w-5 h-5 text-slate-700 dark:text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="text-slate-800 dark:text-slate-200 font-semibold text-sm">{listing.sellerAlias}</span>
              </div>
            </div>

            <p className="text-base text-slate-700 dark:text-slate-300 whitespace-pre-wrap leading-relaxed bg-slate-50 dark:bg-slate-900/50 rounded-xl p-6 border border-slate-200 dark:border-slate-700">{listing.description}</p>

            {/* Discussion Thread Link */}
            {listing.discussionThreadId && listing.discussionThreadTitle && (
              <div className="mt-6 pt-6 border-t-2 border-purple-200 dark:border-purple-900/50">
                <a
                  href={`/forum/thread/${listing.discussionThreadId}`}
                  className="inline-flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-50 to-fuchsia-50 dark:from-purple-900/30 dark:to-fuchsia-900/30 border-2 border-purple-300 dark:border-purple-700 rounded-xl text-purple-700 dark:text-purple-300 hover:from-purple-100 hover:to-fuchsia-100 dark:hover:from-purple-900/50 dark:hover:to-fuchsia-900/50 transition-all shadow-md hover:shadow-lg"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <div>
                    <div className="text-sm font-bold">💬 Join Discussion</div>
                    <div className="text-xs text-purple-600 dark:text-purple-400">{listing.discussionThreadTitle}</div>
                  </div>
                </a>
                <p className="text-xs text-slate-600 dark:text-slate-400 mt-2 ml-1">
                  Ask questions, see reviews, and discuss this service with the community
                </p>
              </div>
            )}
          </div>

            {listing.media && listing.media.length > 0 && (
              <div className="mb-8">
                <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Gallery
                </h3>
              {/* Main Selected Image */}
              <div className="mb-4">
                <div className="relative bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-900 dark:to-slate-950 rounded-2xl overflow-hidden border-2 border-purple-200 dark:border-purple-900/50 shadow-xl" style={{ height: '500px' }}>
                  <img
                    src={listing.media[selectedImageIndex]}
                    alt={`${listing.title} - Image ${selectedImageIndex + 1}`}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.parentElement!.innerHTML = '<div class="flex items-center justify-center h-full text-slate-600">Image unavailable</div>';
                    }}
                  />
                  {/* Image Counter */}
                  <div className="absolute top-4 right-4 bg-black/70 backdrop-blur-sm text-white text-sm font-semibold px-4 py-2 rounded-xl shadow-lg">
                    {selectedImageIndex + 1} / {listing.media.length}
                  </div>
                </div>
              </div>

              {/* Thumbnail Scroll List */}
              {listing.media.length > 1 && (
                <div className="relative">
                  <div className="overflow-x-auto scrollbar-thin">
                    <div className="flex gap-3 pb-2">
                      {listing.media.map((url, idx) => (
                        <button
                          key={idx}
                          onClick={() => setSelectedImageIndex(idx)}
                          className={`flex-shrink-0 w-28 h-28 rounded-xl border-2 transition-all overflow-hidden ${
                            idx === selectedImageIndex
                              ? 'border-purple-500 dark:border-purple-400 ring-4 ring-purple-500/50 scale-105 shadow-lg'
                              : 'border-slate-300 dark:border-slate-700 hover:border-purple-400 dark:hover:border-purple-500 hover:scale-105 shadow-md'
                          }`}
                        >
                          <img
                            src={url}
                            alt={`Thumbnail ${idx + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              e.currentTarget.parentElement!.innerHTML = '<div class="flex items-center justify-center h-full text-slate-600 text-xs">No image</div>';
                            }}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="text-sm text-slate-600 dark:text-slate-400 mb-6 flex items-center gap-2 bg-slate-50 dark:bg-slate-900/50 rounded-lg px-4 py-2 border border-slate-200 dark:border-slate-700">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Listed on {new Date(listing.createdAt).toLocaleDateString()}
          </div>

          {/* Reviews Section with classification gating */}
          {reviews && reviews.length > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-3">Customer Reviews</h3>
              <div className="space-y-3">
                {reviews
                  .filter((review) => {
                    const cls = (review.classification || 'normal').toLowerCase();
                    if (cls === 'advance' || cls === 'superior') {
                      return reviewStats?.advanceSharePerm === true;
                    }
                    return true; // normal/observable visible to all
                  })
                  .map((review) => (
                    <div key={review.id} className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 bg-white dark:bg-slate-800">
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-slate-700 dark:text-slate-300">
                          Buyer: <span className="font-semibold">{review.buyerAlias || 'anonymous'}</span>
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-500">
                          {new Date(review.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="mt-1 text-sm text-slate-600 dark:text-slate-400">Rating: {review.rating}/5</div>
                      {review.classification && (
                        <div className="mt-1 text-xs inline-flex items-center px-2 py-1 rounded bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300">
                          Classification: {review.classification}
                        </div>
                      )}
                      <p className="mt-2 text-slate-800 dark:text-slate-200">{review.comment}</p>
                    </div>
                  ))}
                {reviews.some(r => ['advance','superior'].includes((r.classification||'').toLowerCase())) && reviewStats?.advanceSharePerm === false && (
                  <div className="text-sm text-slate-600 dark:text-slate-400 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                    Some advanced/superior reviews are hidden. Unlock them by earning more high-tier reviews (50%+ advance/superior).
                  </div>
                )}
              </div>
            </div>
          )}

          {bookingSuccess ? (
            <div className="bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-400 dark:border-green-700 rounded-xl p-6 text-green-800 dark:text-green-300 shadow-lg">
              <div className="flex items-center gap-3">
                <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="font-bold text-base">Booking request sent successfully!</p>
                  <p className="text-sm">The seller will review your request and get back to you soon.</p>
                </div>
              </div>
            </div>
          ) : !showBookingForm ? (
            <Button onClick={() => setShowBookingForm(true)} className="w-full text-base py-3 rounded-xl shadow-lg hover:shadow-xl transition-all active:scale-[0.98] font-bold">
              📅 Book This Service
            </Button>
          ) : (
            <BookingForm
              listing={listing}
              onSubmit={handleBooking}
              onCancel={() => setShowBookingForm(false)}
            />
          )}

          {/* Contact Methods */}
          {(listing.whatsapp || listing.wechat || listing.telegram || listing.phoneNumber) && (
            <div className="mt-6 pt-6 border-t-2 border-purple-200 dark:border-purple-900/50">
              <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                Contact Methods
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {listing.whatsapp && (
                  <a
                    href={`https://wa.me/${listing.whatsapp.replace(/[^0-9]/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 border-2 border-green-400 dark:border-green-700 rounded-xl hover:from-green-100 hover:to-emerald-100 dark:hover:from-green-900/50 dark:hover:to-emerald-900/50 transition-all shadow-md hover:shadow-lg"
                  >
                    <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    <div>
                      <div className="text-sm font-bold text-green-700 dark:text-green-300">WhatsApp</div>
                      <div className="text-xs text-green-600 dark:text-green-400">{listing.whatsapp}</div>
                    </div>
                  </a>
                )}

                {listing.wechat && (
                  <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-green-50 to-teal-50 dark:from-green-900/30 dark:to-teal-900/30 border-2 border-green-500 dark:border-green-700 rounded-xl shadow-md">
                    <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.047c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1 .117-.498c1.531-1.043 2.498-2.73 2.498-4.632 0-3.427-2.903-6.2-6.997-6.167zm-2.849 7.356a.968.968 0 0 1-.962-.974c0-.537.432-.974.962-.974.53 0 .961.437.961.974s-.431.974-.961.974zm4.207 0a.968.968 0 0 1-.961-.974c0-.537.432-.974.961-.974.53 0 .962.437.962.974s-.432.974-.962.974z"/>
                    </svg>
                    <div>
                      <div className="text-sm font-bold text-green-700 dark:text-green-300">WeChat</div>
                      <div className="text-xs text-green-600 dark:text-green-400">{listing.wechat}</div>
                    </div>
                  </div>
                )}

                {listing.telegram && (
                  <a
                    href={`https://t.me/${listing.telegram.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/30 dark:to-cyan-900/30 border-2 border-blue-400 dark:border-blue-700 rounded-xl hover:from-blue-100 hover:to-cyan-100 dark:hover:from-blue-900/50 dark:hover:to-cyan-900/50 transition-all shadow-md hover:shadow-lg"
                  >
                    <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                    </svg>
                    <div>
                      <div className="text-sm font-bold text-blue-700 dark:text-blue-300">Telegram</div>
                      <div className="text-xs text-blue-600 dark:text-blue-400">@{listing.telegram.replace('@', '')}</div>
                    </div>
                  </a>
                )}

                {listing.phoneNumber && (
                  <a
                    href={`tel:${listing.phoneNumber}`}
                    className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-slate-50 to-gray-50 dark:from-slate-700 dark:to-gray-800 border-2 border-slate-400 dark:border-slate-600 rounded-xl hover:from-slate-100 hover:to-gray-100 dark:hover:from-slate-600 dark:hover:to-gray-700 transition-all shadow-md hover:shadow-lg"
                  >
                    <svg className="w-6 h-6 text-slate-700 dark:text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <div>
                      <div className="text-sm font-bold text-slate-700 dark:text-slate-300">Phone</div>
                      <div className="text-xs text-slate-600 dark:text-slate-400">{listing.phoneNumber}</div>
                    </div>
                  </a>
                )}
              </div>
            </div>
          )}
        </div>

        {/* User Reviews Section */}
        <div className="bg-white dark:bg-slate-800 border-2 border-purple-200 dark:border-purple-900/50 rounded-2xl p-8 mt-6 shadow-xl">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
            <svg className="w-6 h-6 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            User Reviews
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">Reviews from customers who used this service (showing first 5)</p>

          {reviews.length === 0 ? (
            <div className="text-center py-12 bg-gradient-to-br from-slate-50 to-purple-50/30 dark:from-slate-900 dark:to-purple-950/20 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700">
              <svg className="w-12 h-12 mx-auto mb-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
              <p className="text-base font-semibold text-slate-700 dark:text-slate-300 mb-1">No reviews yet</p>
              <p className="text-sm text-slate-600 dark:text-slate-400">Be the first to review this service!</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Review Summary */}
              <div className="bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-50 dark:from-amber-900/20 dark:via-yellow-900/20 dark:to-amber-900/20 border-2 border-amber-300 dark:border-amber-700 rounded-xl p-6 shadow-md">
                <div className="flex items-center gap-4">
                  <div className="text-3xl font-bold text-amber-600 dark:text-amber-400">
                    {(reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)}
                  </div>
                  <div>
                    <div className="text-amber-400 text-xl mb-1">
                      {'★'.repeat(Math.round(reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length))}
                      {'☆'.repeat(5 - Math.round(reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length))}
                    </div>
                    <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                      Based on {reviews.length} {reviews.length === 1 ? 'review' : 'reviews'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Individual Reviews */}
              {reviews.map((review) => (
                <div key={review.id} className="bg-gradient-to-br from-slate-50 to-purple-50/30 dark:from-slate-900 dark:to-purple-950/20 border-2 border-slate-200 dark:border-slate-700 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-fuchsia-600 rounded-full flex items-center justify-center text-white font-bold shadow-md">
                        {(review.buyerAlias ?? 'A').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <span className="text-slate-800 dark:text-slate-200 font-bold block">{review.buyerAlias ?? 'anonymous'}</span>
                        <span className="text-amber-400">
                          {'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}
                        </span>
                      </div>
                    </div>
                    <span className="text-xs text-slate-600 dark:text-slate-400 bg-slate-200 dark:bg-slate-800 px-3 py-1 rounded-full">
                      {new Date(review.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap leading-relaxed">{review.comment}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BookingForm({
  listing,
  onSubmit,
  onCancel,
}: {
  listing: Listing;
  onSubmit: (buyerAlias: string, scheduledFor: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [buyerAlias, setBuyerAlias] = useState('');
  const [newBuyerAlias, setNewBuyerAlias] = useState('');
  const [hasAlias, setHasAlias] = useState<boolean | null>(null);
  const [registering, setRegistering] = useState(false);
  const [scheduledFor, setScheduledFor] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTimeSlot, setSelectedTimeSlot] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sellerBookings, setSellerBookings] = useState<Booking[]>([]);
  const [loadingBookings, setLoadingBookings] = useState(false);

  useEffect(() => {
    checkBuyerAlias();
    loadSellerBookings();
  }, []);

  async function loadSellerBookings() {
    try {
      setLoadingBookings(true);
      const bookings = await fetchSellerBookings(listing.sellerAlias);
      setSellerBookings(bookings || []);
    } catch (err) {
      console.error('Failed to load seller bookings:', err);
      setSellerBookings([]);
    } finally {
      setLoadingBookings(false);
    }
  }

  async function checkBuyerAlias() {
    try {
      const response = await getMyBuyerAlias();
      if (response.registered && response.alias) {
        setBuyerAlias(response.alias.buyerAlias);
        setHasAlias(true);
      } else {
        setHasAlias(false);
      }
    } catch (err) {
      console.error('Failed to check buyer alias:', err);
      setHasAlias(false);
    }
  }

  async function handleRegisterAlias(e: React.FormEvent) {
    e.preventDefault();
    if (!newBuyerAlias.trim()) {
      setError('Please enter a buyer alias');
      return;
    }

    try {
      setRegistering(true);
      setError(null);
      const response = await registerBuyerAlias(newBuyerAlias.trim());
      if (response.alias) {
        setBuyerAlias(response.alias.buyerAlias);
        setHasAlias(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to register buyer alias');
    } finally {
      setRegistering(false);
    }
  }

  // Check if the selected date is a working day
  const isWorkingDay = (dateStr: string) => {
    if (!listing.workingDays || listing.workingDays.length === 0) {
      return true; // If no working days specified, all days are available
    }

    const date = new Date(dateStr);
    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    return listing.workingDays.includes(dayName);
  };

  // Check if a time slot overlaps with existing bookings
  const isSlotAvailable = (slotStart: string, slotEnd: string): boolean => {
    if (!selectedDate) return true;

    const duration = listing.durationMinutes || 60;

    // Create Date objects for the slot
    const slotStartTime = new Date(`${selectedDate}T${slotStart}:00`);
    const slotEndTime = new Date(`${selectedDate}T${slotEnd}:00`);

    // Check against all active bookings
    for (const booking of sellerBookings) {
      // Only check confirmed, verified, and in_progress bookings
      if (booking.status !== 'confirmed' &&
          booking.status !== 'verified' &&
          booking.status !== 'in_progress') {
        continue;
      }

      // Check if booking is on the same day
      const bookingDate = new Date(booking.scheduledFor);
      const bookingDateStr = bookingDate.toISOString().split('T')[0];

      if (bookingDateStr !== selectedDate) {
        continue;
      }

      // Get booking's listing to know its duration
      const bookingStart = new Date(booking.scheduledFor);
      const bookingEnd = new Date(bookingStart.getTime() + duration * 60000);

      // Check for overlap: (slotStart < bookingEnd) && (slotEnd > bookingStart)
      if (slotStartTime < bookingEnd && slotEndTime > bookingStart) {
        return false; // Slot conflicts with this booking
      }
    }

    return true; // No conflicts
  };

  // Generate time slots for the selected date
  const generateTimeSlots = () => {
    if (!selectedDate) return [];

    const slots: string[] = [];
    const duration = listing.durationMinutes || 60;
    const now = new Date();
    const isToday = selectedDate === now.toISOString().split('T')[0];

    // Use seller's work hours if specified, otherwise default to 9 AM - 6 PM
    const workStart = listing.workStartTime || '09:00';
    const workEnd = listing.workEndTime || '18:00';

    const [startHour, startMin] = workStart.split(':').map(Number);
    const [endHour, endMin] = workEnd.split(':').map(Number);

    const workStartMinutes = startHour * 60 + startMin;
    const workEndMinutes = endHour * 60 + endMin;

    // Generate 30-minute slots within work hours
    for (let totalMinutes = workStartMinutes; totalMinutes < workEndMinutes; totalMinutes += 30) {
      const hour = Math.floor(totalMinutes / 60);
      const minute = totalMinutes % 60;

      const startTime = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

      // Calculate end time
      const endTotalMinutes = totalMinutes + duration;
      const endHour = Math.floor(endTotalMinutes / 60);
      const endMinute = endTotalMinutes % 60;

      // Don't show slots that end after work hours
      if (endTotalMinutes > workEndMinutes) break;

      const endTime = `${endHour.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}`;

      // If it's today, skip slots that have already passed
      if (isToday) {
        const slotStartTime = new Date(`${selectedDate}T${startTime}:00`);
        if (slotStartTime <= now) {
          continue; // Skip past time slots
        }
      }

      // Only add slot if it's available (not booked)
      if (isSlotAvailable(startTime, endTime)) {
        slots.push(`${startTime} - ${endTime}`);
      }
    }

    return slots;
  };

  const timeSlots = generateTimeSlots();
  const selectedDateIsWorking = selectedDate ? isWorkingDay(selectedDate) : true;
  const timeSelectionRequired = !!listing.durationMinutes;
  const noSlotsForDate = timeSelectionRequired && selectedDate && selectedDateIsWorking && timeSlots.length === 0;
  const canSubmit = !submitting
    && !!buyerAlias.trim()
    && (
      !timeSelectionRequired
      || (
        selectedDateIsWorking
        && timeSlots.length > 0
        && !!selectedDate
        && !!selectedTimeSlot
      )
    );

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!buyerAlias.trim()) {
      setError('Please enter your alias');
      return;
    }

    if (timeSelectionRequired) {
      if (!selectedDateIsWorking) {
        setError('Please pick an available day for this service');
        return;
      }
      if (timeSlots.length === 0) {
        setError('No available time slots for this date. Please pick another day.');
        return;
      }
      if (!selectedDate || !selectedTimeSlot) {
        setError('Please select a date and time slot');
        return;
      }
    }

    try {
      setSubmitting(true);
      setError(null);

      // Combine date and time slot if both are selected
      let bookingDateTime = scheduledFor;
      if (selectedDate && selectedTimeSlot) {
        const timeStart = selectedTimeSlot.split(' - ')[0];
        // Create proper ISO datetime with timezone
        const dateTime = new Date(`${selectedDate}T${timeStart}:00`);
        bookingDateTime = dateTime.toISOString();
      } else if (!bookingDateTime) {
        bookingDateTime = new Date().toISOString();
      }

      await onSubmit(buyerAlias.trim(), bookingDateTime);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create booking');
    } finally {
      setSubmitting(false);
    }
  }

  // Show loading state while checking alias
  if (hasAlias === null) {
    return (
      <div className="bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg p-4">
        <div className="text-center text-slate-600 dark:text-slate-400">Checking buyer alias...</div>
      </div>
    );
  }

  // Show registration form if user doesn't have an alias
  if (!hasAlias) {
    return (
      <form onSubmit={handleRegisterAlias} className="bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">Register Buyer Alias</h3>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
          You need to register a buyer alias before making bookings. This alias will be used for all your bookings.
        </p>

        {error && (
          <div className="bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-900 rounded p-3 mb-4 text-red-700 dark:text-red-400 text-sm">{error}</div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Choose Your Buyer Alias *</label>
            <input
              type="text"
              value={newBuyerAlias}
              onChange={(e) => setNewBuyerAlias(e.target.value)}
              className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              placeholder="e.g., buyer123"
              required
              disabled={registering}
            />
            <p className="text-xs text-slate-600 dark:text-slate-500 mt-1">
              This will be your unique identifier for all bookings. Choose wisely - it can be changed later.
            </p>
          </div>

          <div className="flex gap-3">
            <Button type="submit" disabled={registering}>
              {registering ? 'Registering...' : 'Register Alias'}
            </Button>
            <Button type="button" onClick={onCancel} variant="secondary">
              Cancel
            </Button>
          </div>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg p-4">
      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Book This Service</h3>

      {error && (
        <div className="bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-900 rounded p-3 mb-4 text-red-700 dark:text-red-400 text-sm">{error}</div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Your Buyer Alias</label>
          <div className="w-full bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100">
            {buyerAlias}
          </div>
          <p className="text-xs text-slate-600 dark:text-slate-500 mt-1">
            This is your registered buyer alias for bookings
          </p>
        </div>

        {listing.durationMinutes ? (
          <>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Select Date</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => {
                  setSelectedDate(e.target.value);
                  setSelectedTimeSlot(''); // Reset time slot when date changes
                }}
                min={new Date().toISOString().split('T')[0]}
                className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
            </div>

            {selectedDate && (
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Available Time Slots ({listing.durationMinutes < 60
                    ? `${listing.durationMinutes} min`
                    : `${(listing.durationMinutes / 60).toFixed(1)} hr`} service)
                </label>

                {!selectedDateIsWorking ? (
                  <div className="bg-yellow-100 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-800 rounded p-3 text-yellow-800 dark:text-yellow-400 text-sm">
                    <strong>Note:</strong> This service is not available on the selected day.
                    {listing.workingDays && listing.workingDays.length > 0 && (
                      <div className="mt-1">
                        Available days: {listing.workingDays.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ')}
                      </div>
                    )}
                  </div>
                ) : timeSlots.length === 0 ? (
                  <div className="bg-orange-100 dark:bg-orange-900/20 border border-orange-300 dark:border-orange-800 rounded p-3 text-orange-800 dark:text-orange-400 text-sm">
                    <strong>All time slots are booked for this date.</strong>
                    <div className="mt-1 text-xs text-slate-600 dark:text-slate-400">
                      Please select a different date or check back later for availability.
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                    {timeSlots.map((slot) => (
                      <button
                        key={slot}
                        type="button"
                        onClick={() => setSelectedTimeSlot(slot)}
                        className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                          selectedTimeSlot === slot
                            ? 'bg-brand-600 text-white'
                            : 'bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 border border-slate-300 dark:border-slate-600'
                        }`}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Preferred Date & Time (Optional)</label>
            <input
              type="datetime-local"
              value={scheduledFor}
              onChange={(e) => setScheduledFor(e.target.value)}
              className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            />
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <Button type="submit" disabled={!canSubmit}>
            {submitting ? 'Submitting...' : 'Submit Booking Request'}
          </Button>
          <Button type="button" onClick={onCancel} variant="secondary">
            Cancel
          </Button>
        </div>
      </div>
    </form>
  );
}
